import { Component, ElementRef, Input, OnDestroy, OnInit, OnChanges, SimpleChanges, viewChild } from '@angular/core';
import Chart from 'chart.js/auto';
import type { ChartConfiguration, ChartType } from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import datalabelsPlugin from 'chartjs-plugin-datalabels';
import deferredPlugin from 'chartjs-plugin-deferred';

const gaugeNeedlePlugin = {
  id: 'gaugeNeedle',
  afterDatasetsDraw(chart: any) {
    const dataset = chart.config.data.datasets[0];
    if (dataset.needleValue === undefined) return;
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    const arc = meta.data[0];
    if (!arc) return;
    const cx = arc.x, cy = arc.y;
    const outerRadius = arc.outerRadius;
    const max = dataset.needleMax ?? 100;
    const clamped = Math.max(0, Math.min(dataset.needleValue, max));
    const angleFor = (v: number) => Math.PI + (v / max) * Math.PI;
    ctx.save();
    [0, 25, 50, 75, 100].forEach((t: number) => {
      const a = angleFor(t);
      const isEnd = t === 0 || t === 100;
      const x1 = cx + Math.cos(a) * (outerRadius - 1);
      const y1 = cy + Math.sin(a) * (outerRadius - 1);
      const x2 = cx + Math.cos(a) * (outerRadius + 5);
      const y2 = cy + Math.sin(a) * (outerRadius + 5);
      ctx.strokeStyle = isEnd ? '#8a94a6' : '#c7cfda';
      ctx.lineWidth = isEnd ? 2.2 : 1.5;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.font = '600 10px "Reddit Sans", sans-serif';
      ctx.fillStyle = '#8a94a6';
      ctx.textBaseline = 'middle';
      ctx.textAlign = t === 0 ? 'left' : t === 100 ? 'right' : 'center';
      ctx.fillText(String(t), cx + Math.cos(a) * (outerRadius + 12), cy + Math.sin(a) * (outerRadius + 12));
    });
    ctx.restore();
    const angle = angleFor(clamped);
    const needleLength = outerRadius - 16;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.shadowColor = 'rgba(20,30,55,.35)';
    ctx.shadowBlur = 5;
    ctx.shadowOffsetY = 2;
    ctx.beginPath();
    ctx.moveTo(-outerRadius * 0.16, -3.25);
    ctx.lineTo(needleLength, 0);
    ctx.lineTo(-outerRadius * 0.16, 3.25);
    ctx.closePath();
    ctx.fillStyle = '#e8590c';
    ctx.fill();
    ctx.restore();
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, 8.5, 0, Math.PI * 2);
    ctx.fillStyle = '#1D4370';
    ctx.fill();
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();
    ctx.restore();
  },
};

Chart.register(annotationPlugin, datalabelsPlugin, deferredPlugin, gaugeNeedlePlugin);

Chart.defaults.datasets.bar.maxBarThickness = 30;
Chart.defaults.datasets.bar.barPercentage = 0.82;
Chart.defaults.datasets.bar.categoryPercentage = 0.85;
Chart.defaults.datasets.bar.borderRadius = 0;

@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrl: './chart.component.scss',
  standalone: false,
})
export class ChartComponent implements OnInit, OnDestroy, OnChanges {
  readonly canvasRef = viewChild<ElementRef<HTMLCanvasElement>>('canvas');

  @Input() type: ChartType = 'bar';
  @Input() data!: ChartConfiguration['data'];
  @Input() options?: ChartConfiguration['options'];

  private chartInstance?: Chart;
  private renderTimers: ReturnType<typeof setTimeout>[] = [];

  ngOnInit(): void {
    this.createChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const canvas = this.canvasRef()?.nativeElement;
    const instance = this.chartInstance;
    const prevIndexAxis = changes['options']?.previousValue?.indexAxis;
    const currIndexAxis = changes['options']?.currentValue?.indexAxis;
    const indexAxisChanged = prevIndexAxis !== undefined && currIndexAxis !== undefined && prevIndexAxis !== currIndexAxis;

    // Recreate chart if type changes or indexAxis changes (e.g. horizontal ↔ vertical bar)
    if ((changes['type'] || indexAxisChanged) && instance) {
      this.createChart();
      return;
    }
    if (instance && instance.canvas && canvas && canvas.isConnected && (changes['data'] || changes['options'])) {
      instance.data = this.data;
      instance.options = this.chartOptions() as any;
      instance.update();
    }
  }

  ngOnDestroy(): void {
    this.renderTimers.forEach(t => clearTimeout(t));
    this.renderTimers = [];
    this.chartInstance?.destroy();
  }

  hasSelectableLegend(): boolean {
    const legend = this.chartInstance?.legend;
    return !!legend && legend.options.display !== false && (legend.legendItems?.length ?? 0) > 0;
  }

  allLegendItemsVisible(): boolean {
    const items = this.chartInstance?.legend?.legendItems ?? [];
    return items.length > 0 && items.every(item => this.isLegendItemVisible(item));
  }

  noLegendItemsVisible(): boolean {
    const items = this.chartInstance?.legend?.legendItems ?? [];
    return items.length > 0 && items.every(item => !this.isLegendItemVisible(item));
  }

  setLegendItemsVisible(visible: boolean): void {
    const chart = this.chartInstance;
    if (!chart) return;

    for (const item of chart.legend?.legendItems ?? []) {
      if (this.isDataIndexLegendItem(item)) {
        const index = item.index!;
        if (chart.getDataVisibility(index) !== visible) chart.toggleDataVisibility(index);
      } else if (item.datasetIndex !== undefined && chart.isDatasetVisible(item.datasetIndex) !== visible) {
        chart.setDatasetVisibility(item.datasetIndex, visible);
      }
    }
    chart.update();
  }

  private isLegendItemVisible(item: any): boolean {
    const chart = this.chartInstance;
    if (!chart) return false;
    return this.isDataIndexLegendItem(item)
      ? chart.getDataVisibility(item.index)
      : item.datasetIndex !== undefined && chart.isDatasetVisible(item.datasetIndex);
  }

  private isDataIndexLegendItem(item: any): boolean {
    return ['pie', 'doughnut', 'polarArea'].includes((this.chartInstance?.config as any)?.type ?? '')
      && typeof item.index === 'number';
  }

  private chartOptions(): ChartConfiguration['options'] {
    if (this.type !== 'pie' && this.type !== 'doughnut') return this.options;

    const baseOptions = this.options ? { ...this.options } : {};
    const plugins = { ...(baseOptions.plugins ?? {}) };

    // Move legend to right side for all pie and doughnut charts unless explicitly hidden (e.g. gauges)
    if (!plugins.legend) {
      plugins.legend = {
        position: 'right',
      };
    } else if (plugins.legend.display !== false) {
      plugins.legend = {
        ...plugins.legend,
        position: 'right',
      };
    }

    return {
      responsive: true,
      ...baseOptions,
      maintainAspectRatio: false,
      plugins,
    } as ChartConfiguration['options'];
  }

  private createChart(): void {
    const canvas = this.canvasRef()?.nativeElement;
    if (!canvas) return;

    this.chartInstance?.destroy();

    this.chartInstance = new Chart(canvas, {
      type: this.type,
      data: this.data,
      options: this.chartOptions(),
    });

    // chartjs-plugin-deferred holds rendering until the canvas is scrolled into
    // view, which never happens inside modals/dialogs. Force it to appear there.
    this.renderTimers.forEach(t => clearTimeout(t));
    this.renderTimers = [0, 150, 400, 800].map(delay =>
      setTimeout(() => {
        const chart = this.chartInstance;
        if (!chart || !chart.canvas || !chart.canvas.isConnected) return;
        try { chart.update(); } catch { /* chart may have been destroyed */ }
      }, delay)
    );
  }
}
